import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { CHECKOUT_FIELDS, CHECKOUT_FIELD_LABELS, THEMES, THEME_NAMES, type AssortmentItemDto, type I18n, type ImageDto, type ProductListItemDto, type ShopSetupDto, type ShopStorefront } from '@thebb/shared';
import { del, get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner } from '../components/ui';
import { I18nInput, ImageUploadButton, MoneyInput, SingleImage, kr } from './Catalogue';

type Tab = 'setup' | 'assortment' | 'content' | 'delivery' | 'domains';
const DELIVERY_LABELS: Record<string, string> = { parcel_shop: 'Pakkeshop', home: 'Privatadresse', business: 'Erhvervsadresse', office: 'Firmaets adresser (liste)', pickup: 'Afhentning' };
const ACCESS_LABELS: Record<ShopStorefront['access'], string> = { open: 'Åben for alle', login: 'Login (kunder oprettes eller inviteres)', gift_code: 'Gavekode fra kort' };

/** SHO-01 to SHO-08: everything a storefront shows, per shop. */
export function ShopStorefrontPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { can } = useAuth();
  const editable = can('shops.storefront');
  const [tab, setTab] = useState<Tab>('setup');
  const [error, setError] = useState<unknown>(null);
  const setup = useQuery({ queryKey: ['storefront', id], queryFn: () => get<ShopSetupDto>(`/shops/${id}/storefront`) });
  if (!setup.data) return setup.error ? <ErrorBox error={setup.error} /> : <Spinner />;
  const s = setup.data;
  const tabs: [Tab, string][] = [['setup', 'Opsætning'], ['assortment', 'Sortiment og priser'], ['content', 'Sider og forside'], ['delivery', 'Levering'], ['domains', 'Domæner']];
  return (
    <div className="space-y-4">
      <PageTitle
        title={`Webshop: ${s.name}`}
        subtitle={s.ownedHere ? 'Opsat her; 3x ændrer ikke længere navn og datoer' : 'Endnu ikke opsat her; 3x styrer navn og datoer'}
        actions={
          <>
            <Link to="/shops/$id" params={{ id }} className="btn">Til shoppen</Link>
            <a href={`/customers?shopId=${id}`} className="btn">Kunder</a>
            {s.previewUrl && <a className="btn" href={s.previewUrl} target="_blank" rel="noreferrer">Forhåndsvis</a>}
          </>
        }
      />
      <div className="flex gap-1 border-b border-line">
        {tabs.map(([k, label]) => (
          <button key={k} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`} onClick={() => { setTab(k); setError(null); }}>{label}</button>
        ))}
      </div>
      <ErrorBox error={error} />
      {tab === 'setup' && <SetupTab setup={s} editable={editable} onError={setError} />}
      {tab === 'assortment' && <AssortmentTab shopId={id} showPrices={s.storefront.showPrices} gift={s.storefront.giftGrouping} editable={editable} onError={setError} />}
      {tab === 'content' && <ContentTab setup={s} editable={editable} onError={setError} />}
      {tab === 'delivery' && <DeliveryTab setup={s} editable={editable} onError={setError} />}
      {tab === 'domains' && <DomainsTab setup={s} editable={editable} onError={setError} />}
    </div>
  );
}

function useSetupMutation<T>(shopId: string, fn: (body: T) => Promise<ShopSetupDto>, onError: (e: unknown) => void) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (d) => { qc.setQueryData(['storefront', shopId], d); onError(null); }, onError });
}

// --- set-up -----------------------------------------------------------------------------------------

function SetupTab({ setup, editable, onError }: { setup: ShopSetupDto; editable: boolean; onError: (e: unknown) => void }) {
  const [name, setName] = useState(setup.name);
  const [slug, setSlug] = useState(setup.slug ?? '');
  const [opensAt, setOpensAt] = useState(setup.opensAt ?? '');
  const [closesAt, setClosesAt] = useState(setup.closesAt ?? '');
  const [cfg, setCfg] = useState<ShopStorefront>(setup.storefront);
  const [logo, setLogo] = useState<ImageDto | null>(setup.logo);
  const [hero, setHero] = useState<ImageDto | null>(setup.hero);
  const [saved, setSaved] = useState(false);
  const save = useSetupMutation(setup.shopId, () => put<ShopSetupDto>(`/shops/${setup.shopId}/storefront`, { name, slug, opensAt: opensAt || null, closesAt: closesAt || null, storefront: { ...cfg, logoImageId: logo?.id ?? null, heroImageId: hero?.id ?? null } }), onError);
  useEffect(() => { if (save.isSuccess) { setSaved(true); const t = setTimeout(() => setSaved(false), 2000); return () => clearTimeout(t); } }, [save.isSuccess, save.data]);
  const set = (p: Partial<ShopStorefront>) => setCfg({ ...cfg, ...p });
  const toggle = <T extends string>(list: T[], v: T, on: boolean) => (on ? [...new Set([...list, v])] : list.filter((x) => x !== v));
  const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card space-y-3">
          <div className="label">Shop</div>
          <label className="block"><span className="label">Navn</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label className="block"><span className="label">Adresse uden eget domæne</span><div className="flex items-center gap-1 text-sm text-muted">/s/<input className="input" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} /></div></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="label">Åbner</span><input className="input" type="date" value={opensAt} onChange={(e) => setOpensAt(e.target.value)} /></label>
            <label className="block"><span className="label">Lukker</span><input className="input" type="date" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} /></label>
          </div>
          <label className="block">
            <span className="label">Tema</span>
            <select className="input" value={cfg.theme} onChange={(e) => set({ theme: e.target.value as ShopStorefront['theme'] })}>
              {THEMES.map((t) => <option key={t} value={t}>{THEME_NAMES[t]}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="label">Primær farve</span><input type="color" className="h-9 w-full" value={cfg.colors.primary} onChange={(e) => set({ colors: { ...cfg.colors, primary: e.target.value } })} /></label>
            <label className="block"><span className="label">Accent</span><input type="color" className="h-9 w-full" value={cfg.colors.accent} onChange={(e) => set({ colors: { ...cfg.colors, accent: e.target.value } })} /></label>
          </div>
          <div><div className="label">Logo</div><SingleImage image={logo} onChange={setLogo} onError={onError} /></div>
          <div><div className="label">Topbillede</div><SingleImage image={hero} onChange={setHero} onError={onError} /></div>
        </div>

        <div className="card space-y-3 text-sm">
          <div className="label">Adgang og salg</div>
          <label className="block">
            <span className="label">Adgang</span>
            <select className="input" value={cfg.access} onChange={(e) => set({ access: e.target.value as ShopStorefront['access'] })}>
              {Object.entries(ACCESS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          {cfg.access === 'login' && <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.selfRegistration} onChange={(e) => set({ selfRegistration: e.target.checked })} /> Kunder kan selv oprette sig</label>}
          <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.approvalRequired} onChange={(e) => set({ approvalRequired: e.target.checked })} /> Ordrer skal godkendes før de sendes til lageret</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.showPrices} onChange={(e) => set({ showPrices: e.target.checked })} /> Vis priser</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.pricesInclVat} onChange={(e) => set({ pricesInclVat: e.target.checked })} /> Priser vises inkl. moms</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.giftGrouping} onChange={(e) => set({ giftGrouping: e.target.checked })} /> Gavevalg: én gave pr. person, grupperet</label>
          {cfg.giftGrouping && <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.declineAllowed} onChange={(e) => set({ declineAllowed: e.target.checked })} /> Medarbejderen kan fravælge gaven</label>}
          <div>
            <div className="label">Betaling</div>
            <label className="mr-4 inline-flex items-center gap-2"><input type="checkbox" checked={cfg.paymentMethods.includes('invoice')} onChange={(e) => set({ paymentMethods: toggle(cfg.paymentMethods, 'invoice', e.target.checked) })} /> Faktura</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={cfg.paymentMethods.includes('bambora')} onChange={(e) => set({ paymentMethods: toggle(cfg.paymentMethods, 'bambora', e.target.checked) })} /> Kort (Bambora)</label>
          </div>
          {cfg.paymentMethods.includes('bambora') && <label className="block"><span className="label">Bambora merchant (tom: standard)</span><input className="input" value={cfg.bamboraMerchant} onChange={(e) => set({ bamboraMerchant: e.target.value })} /></label>}
          <label className="block"><span className="label">Beløbsgrænse pr. ordre (kr., tom: ingen)</span><MoneyInput className="input" value={cfg.cartLimitMinor} onChange={(cartLimitMinor) => set({ cartLimitMinor })} /></label>
          <div>
            <div className="label">Leveringsmåder</div>
            {Object.entries(DELIVERY_LABELS).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={cfg.deliveryOptions.includes(k as never)} onChange={(e) => set({ deliveryOptions: toggle(cfg.deliveryOptions, k as ShopStorefront['deliveryOptions'][number], e.target.checked) })} /> {v}</label>
            ))}
          </div>
          <div>
            <div className="label">Sprog</div>
            {(['da', 'en'] as const).map((l) => (
              <label key={l} className="mr-4 inline-flex items-center gap-2"><input type="checkbox" checked={cfg.languages.includes(l)} onChange={(e) => { const languages = toggle(cfg.languages, l, e.target.checked); if (languages.length) set({ languages, defaultLanguage: languages.includes(cfg.defaultLanguage) ? cfg.defaultLanguage : languages[0]! }); }} /> {l === 'da' ? 'Dansk' : 'Engelsk'}</label>
            ))}
          </div>
        </div>

        <div className="card space-y-3 text-sm">
          <div className="label">Kassen</div>
          {CHECKOUT_FIELDS.map((f) => (
            <div key={f} className="flex items-center justify-between gap-2">
              <span>{CHECKOUT_FIELD_LABELS[f]}</span>
              <select className="input w-32" value={cfg.checkoutFields[f] ?? 'hidden'} onChange={(e) => set({ checkoutFields: { ...cfg.checkoutFields, [f]: e.target.value as 'required' } })}>
                <option value="hidden">skjult</option>
                <option value="optional">valgfri</option>
                <option value="required">krævet</option>
              </select>
            </div>
          ))}
          {cfg.checkoutFields.department && cfg.checkoutFields.department !== 'hidden' && (
            <label className="block"><span className="label">Afdelinger (én pr. linje; tom: fri tekst)</span><textarea className="input" rows={3} defaultValue={cfg.departments.join('\n')} onBlur={(e) => set({ departments: lines(e.target.value) })} /></label>
          )}
          {cfg.checkoutFields.budget && cfg.checkoutFields.budget !== 'hidden' && (
            <label className="block"><span className="label">Budgetter / omkostningssteder (én pr. linje)</span><textarea className="input" rows={3} defaultValue={cfg.budgets.join('\n')} onBlur={(e) => set({ budgets: lines(e.target.value) })} /></label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="label">Kontakt-mail</span><input className="input" value={cfg.contactEmail} onChange={(e) => set({ contactEmail: e.target.value })} /></label>
            <label className="block"><span className="label">Kontakt-telefon</span><input className="input" value={cfg.contactPhone} onChange={(e) => set({ contactPhone: e.target.value })} /></label>
          </div>
          <label className="block"><span className="label">Handelsbetingelser (URL)</span><input className="input" value={cfg.termsUrl} onChange={(e) => set({ termsUrl: e.target.value })} /></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.seoIndex} onChange={(e) => set({ seoIndex: e.target.checked })} /> Må findes af søgemaskiner</label>
        </div>
      </div>
      <div className="card grid gap-4 text-sm lg:grid-cols-3">
        <div className="space-y-2">
          <div className="label">Bestilling i webshoppen</div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.checkoutEnabled} onChange={(e) => set({ checkoutEnabled: e.target.checked })} /> Kunderne kan bestille her (slå først til, når shoppen er flyttet fra 3x)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={cfg.requireTerms} onChange={(e) => set({ requireTerms: e.target.checked })} /> Handelsbetingelser skal accepteres (kræver URL)</label>
        </div>
        <label className="block">
          <span className="label">Godkendere (én e-mail pr. linje; bruges når ordrer skal godkendes)</span>
          <textarea className="input" rows={3} defaultValue={cfg.approverEmails.join('\n')} onBlur={(e) => set({ approverEmails: e.target.value.split(/[\n,;]/).map((x) => x.trim()).filter(Boolean) })} />
        </label>
        <div><div className="label">Tekst i ordrebekræftelsen (HTML)</div><I18nInput value={cfg.confirmationText} onChange={(confirmationText) => set({ confirmationText })} multiline rows={3} /></div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card"><div className="label">Velkomsttekst (HTML)</div><I18nInput value={cfg.welcome} onChange={(welcome) => set({ welcome })} multiline rows={5} /></div>
        <div className="card"><div className="label">Sidefod (HTML)</div><I18nInput value={cfg.footer} onChange={(footer) => set({ footer })} multiline rows={5} /></div>
      </div>
      {editable && (
        <div className="flex justify-end">
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate(undefined as never)}>{save.isPending ? 'Gemmer…' : saved ? 'Gemt' : 'Gem opsætning'}</button>
        </div>
      )}
    </div>
  );
}

// --- assortment -------------------------------------------------------------------------------------

function AssortmentTab({ shopId, showPrices, gift, editable, onError }: { shopId: string; showPrices: boolean; gift: boolean; editable: boolean; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['assortment', shopId], queryFn: () => get<AssortmentItemDto[]>(`/shops/${shopId}/storefront/assortment`) });
  const [items, setItems] = useState<AssortmentItemDto[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (data.data) { setItems(data.data); setDirty(false); } }, [data.data]);
  const save = useMutation({
    mutationFn: (list: AssortmentItemDto[]) => put<AssortmentItemDto[]>(`/shops/${shopId}/storefront/assortment`, { items: list.map((x, i) => ({ productId: x.productId, sortOrder: (i + 1) * 10, active: x.active, groupName: x.groupName, prices: x.variants.map((v) => ({ variantId: v.variantId, priceMinor: v.shopPriceMinor })) })) }),
    onSuccess: (d) => { qc.setQueryData(['assortment', shopId], d); onError(null); },
    onError,
  });
  const add = useMutation({
    mutationFn: (productIds: string[]) => post<AssortmentItemDto[]>(`/shops/${shopId}/storefront/assortment/add`, { productIds }),
    onSuccess: (d) => { qc.setQueryData(['assortment', shopId], d); setAdding(false); },
    onError,
  });
  if (!items) return data.error ? <ErrorBox error={data.error} /> : <Spinner />;
  const update = (i: number, p: Partial<AssortmentItemDto>) => { setItems(items.map((x, j) => (j === i ? { ...x, ...p } : x))); setDirty(true); };
  const move = (i: number, d: number) => {
    const list = [...items];
    const [x] = list.splice(i, 1);
    list.splice(i + d, 0, x!);
    setItems(list);
    setDirty(true);
  };
  const groups = [...new Set(items.map((x) => x.groupName).filter(Boolean))] as string[];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">{items.length} produkter. {showPrices ? 'Tom shop-pris betyder produktets egen pris.' : 'Priser vises ikke i denne shop.'}</div>
        {editable && (
          <div className="flex gap-2">
            <button className="btn" onClick={() => setAdding(true)}>Tilføj produkter</button>
            <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate(items)}>{save.isPending ? 'Gemmer…' : 'Gem sortiment'}</button>
          </div>
        )}
      </div>
      <datalist id="groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
      <div className="card p-0">
        <table className="table text-sm">
          <thead><tr><th className="w-16"></th><th className="w-12"></th><th>Produkt</th>{gift && <th>Gruppe</th>}<th>Varianter og shop-pris</th><th>Vises</th><th></th></tr></thead>
          <tbody>
            {items.map((x, i) => (
              <tr key={x.productId} className={x.active ? '' : 'opacity-60'}>
                <td className="whitespace-nowrap text-muted">
                  <button disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" /></button>
                  <button disabled={i === items.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" /></button>
                </td>
                <td>{x.image ? <img src={x.image.thumbUrl} alt="" className="h-10 w-10 object-contain" /> : x.imageUrl ? <img src={x.imageUrl} alt="" className="h-10 w-10 object-contain" /> : null}</td>
                <td><Link to="/products/$id" params={{ id: x.productId }} className="font-medium text-primary hover:underline">{x.title}</Link><div className="font-mono text-xs text-muted">{x.number}</div></td>
                {gift && <td><input className="input w-40" list="groups" value={x.groupName ?? ''} onChange={(e) => update(i, { groupName: e.target.value || null })} /></td>}
                <td>
                  {x.variants.map((v, k) => (
                    <div key={v.variantId} className="flex items-center gap-2 py-0.5">
                      <span className="w-40 truncate font-mono text-xs">{v.sku}{v.title ? ` · ${v.title}` : ''}</span>
                      <span className="w-20 text-right text-xs text-muted">{kr(v.basePriceMinor)}</span>
                      <MoneyInput value={v.shopPriceMinor} onChange={(shopPriceMinor) => update(i, { variants: x.variants.map((y, m) => (m === k ? { ...y, shopPriceMinor } : y)) })} />
                    </div>
                  ))}
                </td>
                <td><input type="checkbox" checked={x.active} onChange={(e) => update(i, { active: e.target.checked })} /></td>
                <td><button className="text-muted" onClick={() => { setItems(items.filter((_, j) => j !== i)); setDirty(true); }}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-muted">Ingen produkter i shoppen endnu.</td></tr>}
          </tbody>
        </table>
      </div>
      {adding && <AddProductsModal existing={items.map((x) => x.productId)} onClose={() => setAdding(false)} onAdd={(ids) => add.mutate(ids)} busy={add.isPending} />}
    </div>
  );
}

function AddProductsModal({ existing, onClose, onAdd, busy }: { existing: string[]; onClose: () => void; onAdd: (ids: string[]) => void; busy: boolean }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const list = useQuery({ queryKey: ['products', 'pick', q], queryFn: () => get<{ items: ProductListItemDto[] }>(`/catalogue/products?perPage=100${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`) });
  return (
    <Modal title="Tilføj produkter" onClose={onClose}>
      <div className="space-y-2">
        <input className="input" autoFocus placeholder="Søg navn, varenr. eller SKU" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="max-h-96 overflow-y-auto">
          {list.data?.items.map((p) => {
            const there = existing.includes(p.id);
            return (
              <label key={p.id} className={`flex items-center gap-2 border-b border-line py-1 text-sm ${there ? 'text-muted' : ''}`}>
                <input type="checkbox" disabled={there} checked={there || picked.includes(p.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, p.id] : picked.filter((x) => x !== p.id))} />
                {p.image ? <img src={p.image.thumbUrl} alt="" className="h-8 w-8 object-contain" /> : <span className="h-8 w-8" />}
                <span className="flex-1">{p.title}</span>
                <span className="font-mono text-xs">{p.number}</span>
                {!p.active && <Badge>inaktiv</Badge>}
              </label>
            );
          })}
        </div>
        <div className="flex justify-end"><button className="btn btn-primary" disabled={!picked.length || busy} onClick={() => onAdd(picked)}>Tilføj {picked.length || ''}</button></div>
      </div>
    </Modal>
  );
}

// --- pages and slides --------------------------------------------------------------------------------

type PageForm = { id: string | null; slug: string; title: I18n; body: I18n; showInMenu: boolean; sortOrder: number; active: boolean };

function ContentTab({ setup, editable, onError }: { setup: ShopSetupDto; editable: boolean; onError: (e: unknown) => void }) {
  const base = `/shops/${setup.shopId}/storefront`;
  const [page, setPage] = useState<PageForm | null>(null);
  const savePage = useSetupMutation(setup.shopId, (f: PageForm) => { const { id, ...body } = f; return id ? put<ShopSetupDto>(`${base}/pages/${id}`, body) : post<ShopSetupDto>(`${base}/pages`, body); }, onError);
  const removePage = useSetupMutation(setup.shopId, (id: string) => del<ShopSetupDto>(`${base}/pages/${id}`), onError);
  const addSlide = useSetupMutation(setup.shopId, (img: ImageDto) => post<ShopSetupDto>(`${base}/slides`, { imageId: img.id, title: {}, sortOrder: (setup.slides.length + 1) * 10 }), onError);
  const saveSlide = useSetupMutation(setup.shopId, (x: ShopSetupDto['slides'][number]) => put<ShopSetupDto>(`${base}/slides/${x.id}`, { imageId: x.image.id, title: x.title, linkUrl: x.linkUrl, sortOrder: x.sortOrder, active: x.active }), onError);
  const removeSlide = useSetupMutation(setup.shopId, (id: string) => del<ShopSetupDto>(`${base}/slides/${id}`), onError);
  useEffect(() => { if (savePage.isSuccess) setPage(null); }, [savePage.isSuccess, savePage.data]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card space-y-2">
        <div className="flex items-center justify-between">
          <div className="label">Sider</div>
          {editable && <button className="btn" onClick={() => setPage({ id: null, slug: '', title: {}, body: {}, showInMenu: true, sortOrder: (setup.pages.length + 1) * 10, active: true })}>Ny side</button>}
        </div>
        {setup.pages.map((p) => (
          <div key={p.id} className="flex items-center justify-between border-b border-line py-1 text-sm">
            <button className="text-primary hover:underline" disabled={!editable} onClick={() => setPage(p)}>{p.title.da || p.slug}</button>
            <span className="text-xs text-muted">/{p.slug}{p.showInMenu ? ' · i menu' : ''}{p.active ? '' : ' · skjult'}</span>
          </div>
        ))}
        {setup.pages.length === 0 && <div className="text-sm text-muted">Ingen sider. Typisk: Om os, Levering, Handelsbetingelser, FAQ.</div>}
      </div>
      <div className="card space-y-2">
        <div className="flex items-center justify-between">
          <div className="label">Forside-slides</div>
          {editable && <ImageUploadButton label="Tilføj slide" onUploaded={(img) => addSlide.mutate(img)} onError={onError} />}
        </div>
        {setup.slides.map((x) => (
          <div key={x.id} className="flex gap-3 border-b border-line py-2">
            <img src={x.image.thumbUrl} alt="" className="h-16 w-28 rounded object-cover" />
            <div className="flex-1 space-y-1 text-sm">
              <input className="input" placeholder="Overskrift (dansk)" defaultValue={x.title.da ?? ''} onBlur={(e) => e.target.value !== (x.title.da ?? '') && saveSlide.mutate({ ...x, title: { ...x.title, da: e.target.value } })} />
              <input className="input" placeholder="Link (valgfrit)" defaultValue={x.linkUrl ?? ''} onBlur={(e) => (e.target.value || null) !== x.linkUrl && saveSlide.mutate({ ...x, linkUrl: e.target.value || null })} />
              <label className="flex items-center gap-2"><input type="checkbox" checked={x.active} onChange={(e) => saveSlide.mutate({ ...x, active: e.target.checked })} /> Vises</label>
            </div>
            {editable && <button className="text-muted" onClick={() => removeSlide.mutate(x.id)}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
      {page && (
        <Modal title={page.id ? 'Redigér side' : 'Ny side'} onClose={() => setPage(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="block"><span className="label">Adresse</span><input className="input" value={page.slug} onChange={(e) => setPage({ ...page, slug: e.target.value.toLowerCase() })} /></label>
              <label className="block"><span className="label">Rækkefølge</span><input className="input" type="number" value={page.sortOrder} onChange={(e) => setPage({ ...page, sortOrder: Number(e.target.value) || 0 })} /></label>
            </div>
            <div><div className="label">Titel</div><I18nInput value={page.title} onChange={(title) => setPage({ ...page, title })} /></div>
            <div><div className="label">Indhold (HTML)</div><I18nInput value={page.body} onChange={(body) => setPage({ ...page, body })} multiline rows={10} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={page.showInMenu} onChange={(e) => setPage({ ...page, showInMenu: e.target.checked })} /> I menuen</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={page.active} onChange={(e) => setPage({ ...page, active: e.target.checked })} /> Synlig</label>
            <div className="flex justify-between">
              {page.id ? <button className="btn btn-danger" onClick={() => { removePage.mutate(page.id!); setPage(null); }}>Slet</button> : <span />}
              <button className="btn btn-primary" disabled={savePage.isPending || !page.slug} onClick={() => savePage.mutate(page)}>Gem</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- delivery ---------------------------------------------------------------------------------------

type Address = Omit<ShopSetupDto['deliveryAddresses'][number], 'id'> & { id: string | null };

function DeliveryTab({ setup, editable, onError }: { setup: ShopSetupDto; editable: boolean; onError: (e: unknown) => void }) {
  const base = `/shops/${setup.shopId}/storefront`;
  const [addr, setAddr] = useState<Address | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [replace, setReplace] = useState(false);
  const [price, setPrice] = useState({ method: 'home', countryCode: 'DK', minOrderMinor: 0 as number | null, priceMinor: 0 as number | null });
  const saveAddr = useSetupMutation(setup.shopId, (a: Address) => { const { id, ...body } = a; return id ? put<ShopSetupDto>(`${base}/addresses/${id}`, body) : post<ShopSetupDto>(`${base}/addresses`, body); }, onError);
  const removeAddr = useSetupMutation(setup.shopId, (id: string) => del<ShopSetupDto>(`${base}/addresses/${id}`), onError);
  const importAddr = useSetupMutation(setup.shopId, (b: { csv: string; replace: boolean }) => post<ShopSetupDto>(`${base}/addresses/import`, b), onError);
  const addPrice = useSetupMutation(setup.shopId, () => post<ShopSetupDto>(`${base}/shipping-prices`, { ...price, minOrderMinor: price.minOrderMinor ?? 0, priceMinor: price.priceMinor ?? 0 }), onError);
  const removePrice = useSetupMutation(setup.shopId, (id: string) => del<ShopSetupDto>(`${base}/shipping-prices/${id}`), onError);
  useEffect(() => { if (saveAddr.isSuccess) setAddr(null); }, [saveAddr.isSuccess, saveAddr.data]);
  useEffect(() => { if (importAddr.isSuccess) setCsv(null); }, [importAddr.isSuccess, importAddr.data]);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card space-y-2">
        <div className="flex items-center justify-between">
          <div className="label">Firmaets leveringsadresser ({setup.deliveryAddresses.length})</div>
          {editable && (
            <div className="flex gap-2">
              <button className="btn" onClick={() => setCsv('')}>Importér CSV</button>
              <button className="btn" onClick={() => setAddr({ id: null, name: '', company: null, department: null, street1: '', street2: null, zip: '', city: '', countryCode: 'DK', customerNumber: null, contact: null, email: null, sortOrder: (setup.deliveryAddresses.length + 1) * 10 })}>Ny adresse</button>
            </div>
          )}
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          {setup.deliveryAddresses.map((a) => (
            <div key={a.id} className="flex items-center justify-between border-b border-line py-1 text-sm">
              <button className="text-left text-primary hover:underline" disabled={!editable} onClick={() => setAddr(a)}>{a.name}</button>
              <span className="text-xs text-muted">{a.street1}, {a.zip} {a.city}{a.customerNumber ? ` · ${a.customerNumber}` : ''}</span>
            </div>
          ))}
        </div>
        {setup.deliveryAddresses.length === 0 && <div className="text-sm text-muted">Bruges når "Firmaets adresser" er valgt som leveringsmåde.</div>}
      </div>
      <div className="card space-y-2">
        <div className="label">Fragtpriser kunden betaler</div>
        <table className="table text-sm">
          <thead><tr><th>Måde</th><th>Land</th><th className="text-right">Fra ordrebeløb</th><th className="text-right">Pris</th><th></th></tr></thead>
          <tbody>
            {setup.shippingPrices.map((p) => (
              <tr key={p.id}>
                <td>{DELIVERY_LABELS[p.method] ?? p.method}</td>
                <td>{p.countryCode}</td>
                <td className="text-right tabular-nums">{kr(p.minOrderMinor)}</td>
                <td className="text-right tabular-nums">{p.priceMinor === 0 ? 'gratis' : kr(p.priceMinor)}</td>
                <td>{editable && <button className="text-muted" onClick={() => removePrice.mutate(p.id)}><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
            {setup.shippingPrices.length === 0 && <tr><td colSpan={5} className="text-muted">Ingen priser: levering er gratis.</td></tr>}
          </tbody>
        </table>
        {editable && (
          <div className="flex flex-wrap items-end gap-2 text-sm">
            <select className="input w-40" value={price.method} onChange={(e) => setPrice({ ...price, method: e.target.value })}>{Object.entries(DELIVERY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            <input className="input w-16" value={price.countryCode} maxLength={2} onChange={(e) => setPrice({ ...price, countryCode: e.target.value.toUpperCase() })} />
            <label>fra <MoneyInput value={price.minOrderMinor} onChange={(minOrderMinor) => setPrice({ ...price, minOrderMinor })} /></label>
            <label>pris <MoneyInput value={price.priceMinor} onChange={(priceMinor) => setPrice({ ...price, priceMinor })} /></label>
            <button className="btn" onClick={() => addPrice.mutate(undefined as never)}>Tilføj</button>
          </div>
        )}
        <p className="text-xs text-muted">Den laveste matchende pris bruges: fx Privatadresse 49 kr. og fra 500 kr. 0 kr.</p>
      </div>
      {addr && (
        <Modal title={addr.id ? 'Leveringsadresse' : 'Ny leveringsadresse'} onClose={() => setAddr(null)}>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {([['name', 'Navn (vises for kunden)'], ['company', 'Firma'], ['department', 'Afdeling'], ['street1', 'Adresse'], ['street2', 'Adresse 2'], ['zip', 'Postnr.'], ['city', 'By'], ['countryCode', 'Land'], ['customerNumber', 'Kundenr.'], ['contact', 'Kontakt'], ['email', 'E-mail']] as [keyof Address, string][]).map(([k, label]) => (
              <label key={k} className="block"><span className="label">{label}</span><input className="input" value={(addr[k] as string | null) ?? ''} onChange={(e) => setAddr({ ...addr, [k]: e.target.value || null })} /></label>
            ))}
            <div className="col-span-2 flex justify-between">
              {addr.id ? <button className="btn btn-danger" onClick={() => { removeAddr.mutate(addr.id!); setAddr(null); }}>Slet</button> : <span />}
              <button className="btn btn-primary" disabled={!addr.name || !addr.street1 || !addr.zip || !addr.city} onClick={() => saveAddr.mutate({ ...addr, countryCode: addr.countryCode || 'DK' })}>Gem</button>
            </div>
          </div>
        </Modal>
      )}
      {csv !== null && (
        <Modal title="Importér leveringsadresser" onClose={() => setCsv(null)}>
          <div className="space-y-2 text-sm">
            <p className="text-muted">Første linje er overskrifter: navn, adresse, postnr, by, og valgfrit firma, afdeling, adresse2, land, kundenr, kontakt, email. Semikolon eller komma.</p>
            <input type="file" accept=".csv,text/csv" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setCsv(await f.text()); }} />
            <textarea className="input font-mono text-xs" rows={8} value={csv} onChange={(e) => setCsv(e.target.value)} />
            <label className="flex items-center gap-2"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> Erstat de nuværende adresser</label>
            <div className="flex justify-end"><button className="btn btn-primary" disabled={!csv.trim() || importAddr.isPending} onClick={() => importAddr.mutate({ csv, replace })}>Importér</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// --- domains ----------------------------------------------------------------------------------------

function TlsBadge({ d }: { d: ShopSetupDto['domains'][number] }) {
  if (d.tlsStatus === 'active') return <span title={d.tlsExpiresAt ? `Certifikat til ${new Date(d.tlsExpiresAt).toLocaleDateString('da-DK')}, fornyes automatisk` : undefined}><Badge tone="green">HTTPS aktiv</Badge></span>;
  if (d.tlsStatus === 'dns') return <Badge tone="amber">venter på DNS</Badge>;
  if (d.tlsStatus === 'error') return <Badge tone="red">certifikat fejlede</Badge>;
  return <Badge>certifikat på vej</Badge>;
}

function DomainsTab({ setup, editable, onError }: { setup: ShopSetupDto; editable: boolean; onError: (e: unknown) => void }) {
  const base = `/shops/${setup.shopId}/storefront`;
  const [host, setHost] = useState('');
  const add = useSetupMutation(setup.shopId, () => post<ShopSetupDto>(`${base}/domains`, { host: host.trim(), primary: setup.domains.length === 0 }), onError);
  const remove = useSetupMutation(setup.shopId, (id: string) => del<ShopSetupDto>(`${base}/domains/${id}`), onError);
  useEffect(() => { if (add.isSuccess) setHost(''); }, [add.isSuccess, add.data]);
  return (
    <div className="card max-w-2xl space-y-3 text-sm">
      <div className="label">Domæner</div>
      {setup.domains.map((d) => (
        <div key={d.id} className="flex items-center justify-between border-b border-line py-1">
          <span className="min-w-0">
            <a href={`https://${d.host}`} target="_blank" rel="noreferrer" className="hover:underline">{d.host}</a> {d.primary && <Badge tone="blue">primært</Badge>} <TlsBadge d={d} />
            {d.tlsStatus !== 'active' && d.tlsMessage && <span className="block text-xs text-muted">{d.tlsMessage}</span>}
          </span>
          {editable && <button className="text-muted" onClick={() => remove.mutate(d.id)}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      {setup.domains.length === 0 && <div className="text-muted">Intet eget domæne. Shoppen kan altid nås på /s/{setup.slug}.</div>}
      {editable && (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (host.trim()) add.mutate(undefined as never); }}>
          <input className="input" placeholder="fx julegave.firma.dk" value={host} onChange={(e) => setHost(e.target.value)} />
          <button className="btn">Tilføj</button>
        </form>
      )}
      <p className="text-xs text-muted">Domænet skal pege på serveren: CNAME til thebb.samsinnovation.dev eller A-post til 195.201.85.160. Serveren tjekker hvert minut og udsteder selv et Let's Encrypt-certifikat, så snart DNS peger rigtigt; det tager typisk et til to minutter. Underdomæner af samsinnovation.dev peger her allerede.</p>
    </div>
  );
}
